import {
  BaseEdge,
  EdgeLabelRenderer,
  type EdgeProps,
  Position,
  type ReactFlowState,
  getBezierPath,
  useReactFlow,
  useStore,
} from '@xyflow/react';
import { shallow } from 'zustand/shallow';
import { useI18n } from '@/providers/i18n';
import { memo, useCallback, useMemo } from 'react';
import { computeModelCapabilities } from '@/lib/edges/frame-role';

import { useProject } from '@/providers/project';
import { useUser } from '@/hooks/use-user';
import { useNodeOperations } from '@/providers/node-operations';
import { MiniEdgeMenu } from '@/components/edges/mini-menu';
import { nanoid } from 'nanoid';
import { getIsCanvasPositionInteractingSync } from '@/components/canvas/utils/drag-state-store';
import { useWhyRender } from '@/lib/debug/use-perf';
import { perfEnabled } from '@/lib/debug/canvas-perf';

const EDGE_PERF_EMPTY: Record<string, unknown> = {};

// Narrow slice of an internal node that AnimatedEdge actually needs.
// Subscribing to this (via useStore + shallow) instead of the full
// `useInternalNode(...)` means we DON'T re-render the edge when unrelated
// internal-node fields change (e.g. `measured`, `parentId`, `zIndex`).
type EdgeNodeSlice = {
  positionAbsolute: { x: number; y: number };
  handleBounds: any;
  type?: string;
  selected?: boolean;
  locked?: boolean;
  fileType?: string;
  contentType?: string;
  model?: string;
  frameMode?: string;
} | null;

function makeEdgeNodeSelector(nodeId: string) {
  return (s: ReactFlowState): EdgeNodeSlice => {
    const n = s.nodeLookup.get(nodeId);
    if (!n) return null;
    const data = (n as any).data ?? {};
    return {
      positionAbsolute: n.internals.positionAbsolute,
      handleBounds: n.internals.handleBounds,
      type: (n as any).type,
      selected: (n as any).selected,
      locked: data.locked,
      fileType: data.fileType,
      contentType: data?.content?.type,
      model: data.model,
      frameMode: data.frameMode,
    };
  };
}

// Build the (sourceNode, targetNode) shape that getEdgeParams expects from a
// narrow selector slice — keeps the rest of the file unchanged.
function getEdgeCoords(slice: NonNullable<EdgeNodeSlice>) {
  return {
    internals: {
      positionAbsolute: slice.positionAbsolute,
      handleBounds: slice.handleBounds,
    },
  } as any;
}

const getHandleCoordsByPosition = (
  node: any,
  handlePosition: Position
) => {
  const handleType = handlePosition === Position.Left ? 'target' : 'source';

  const handle = node.internals.handleBounds?.[handleType]?.find(
    (h: any) => h.position === handlePosition
  );

  if (!handle) {
    return [0, 0];
  }

  let offsetX = handle.width / 2;
  let offsetY = handle.height / 2;

  switch (handlePosition) {
    case Position.Left:
      offsetX = 0;
      break;
    case Position.Right:
      offsetX = handle.width;
      break;
    case Position.Top:
      offsetY = 0;
      break;
    case Position.Bottom:
      offsetY = handle.height;
      break;
    default:
      throw new Error(`Invalid handle position: ${handlePosition}`);
  }

  const x = node.internals.positionAbsolute.x + handle.x + offsetX;
  const y = node.internals.positionAbsolute.y + handle.y + offsetY;

  return [x, y];
};

const getEdgeParams = (
  source: any,
  target: any,
) => {
  const sourcePos = Position.Right;
  const [sx, sy] = getHandleCoordsByPosition(source, sourcePos);
  const targetPos = Position.Left;
  const [tx, ty] = getHandleCoordsByPosition(target, targetPos);

  return {
    sx,
    sy,
    tx,
    ty,
    sourcePos,
    targetPos,
  };
};

/**
 * Lazily-mounted sub-component: only rendered when the edge is selected.
 * Keeps useProject / useUser / useNodeOperations / useI18n out of the hot path.
 */
const SelectedEdgeMenu = memo(({
  edgeId,
  source,
  target,
  labelX,
  labelY,
  isSourceLocked,
  isTargetLocked,
}: {
  edgeId: string;
  source: string;
  target: string;
  labelX: number;
  labelY: number;
  isSourceLocked: boolean;
  isTargetLocked: boolean;
}) => {
  const { t } = useI18n();
  const { setEdges, deleteElements } = useReactFlow();
  const { addNode } = useNodeOperations();
  const project = useProject();
  const { user } = useUser();

  const isOriginalOwner = (() => {
    try {
      const originOwnerId = (project as any)?.originOwnerUserId ?? (project as any)?.userId;
      return Boolean(user?.id) && String(user?.id) === String(originOwnerId ?? '');
    } catch {
      return false;
    }
  })();

  if ((isTargetLocked || isSourceLocked) && !isOriginalOwner) return null;

  return (
    <EdgeLabelRenderer>
      <div
        style={{
          position: 'absolute',
          transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY + 18}px)`,
          pointerEvents: 'auto',
        }}
      >
        <MiniEdgeMenu
          onQuickInsert={(type) => {
            try {
              const newId = addNode(type, {
                position: { x: labelX, y: labelY },
                selected: true,
              });
              try { deleteElements({ edges: [{ id: edgeId }] }); } catch {}
              setEdges((eds: any[]) => (eds as any).concat([
                { id: nanoid(), source, target: newId, type: 'animated' } as any,
                { id: nanoid(), source: newId, target, type: 'animated' } as any,
              ]));
            } catch {}
          }}
          onDelete={(e?: any) => {
            try {
              if (e?.stopPropagation) e.stopPropagation();
            } catch {}
            try { deleteElements({ edges: [{ id: edgeId }] }); } catch {}
          }}
          labels={{
            insert: t('Insert node'),
            delete: t('Delete edge'),
            text: t('Text'),
            image: t('Image'),
            audio: t('Audio'),
            video: t('Video'),
          }}
        />
      </div>
    </EdgeLabelRenderer>
  );
});
SelectedEdgeMenu.displayName = 'SelectedEdgeMenu';

function shallowObjectEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (!a || !b) return a === b;
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const key of ak) {
    if (!Object.is(a[key], b[key])) return false;
  }
  return true;
}

function areAnimatedEdgePropsEqual(prev: EdgeProps, next: EdgeProps): boolean {
  return (
    prev.id === next.id &&
    prev.source === next.source &&
    prev.target === next.target &&
    prev.selected === next.selected &&
    prev.markerEnd === next.markerEnd &&
    shallowObjectEqual(prev.data, next.data) &&
    shallowObjectEqual(prev.style, next.style)
  );
}

export const AnimatedEdge = memo(({
  id,
  source,
  target,
  markerEnd,
  style,
  data,
  selected,
}: EdgeProps) => {
  // IMPORTANT: All hooks must run unconditionally on every render — early
  // returns below are safe ONLY because they happen AFTER every hook call.
  //
  // Perf (Phase 6.1): replace `useInternalNode` with a narrow store selector
  // + shallow equality. This means the edge re-renders only when fields that
  // *actually affect* edge rendering change (position, handle bounds, type,
  // selection, locked, fileType/contentType, model, frameMode) — not on
  // unrelated `measured` / `parentId` / `zIndex` etc. internal updates.
  const sourceSelector = useCallback(makeEdgeNodeSelector(source), [source]);
  const targetSelector = useCallback(makeEdgeNodeSelector(target), [target]);
  const sourceSlice = useStore(sourceSelector, shallow);
  const targetSlice = useStore(targetSelector, shallow);
  // Perf: read the global drag/group-interaction flag NON-reactively (sync getter)
  // instead of subscribing. Subscribing made EVERY edge (631 on large canvases)
  // re-render whenever the flag flips at drag start/stop — the dominant cost of
  // "lag on a single drag". Only edges adjacent to the dragged node re-render
  // mid-drag (their source/target slice changes); those reads pick up the flag
  // here and take the downgraded path. Non-adjacent edges don't re-render at all
  // (they never needed the downgrade since their geometry didn't change).
  const isInteracting = getIsCanvasPositionInteractingSync();
  // Memo for model capability lookup. Lifted above all early returns so this
  // hook is called on every render (React rules-of-hooks). Reads target node
  // model id defensively because targetSlice may be null this render.
  const targetModelId = targetSlice?.model;
  const { textOnlyModel } = useMemo(
    () => computeModelCapabilities(targetModelId),
    [targetModelId]
  );

  // Perf 归因：边的重渲染由 store slice（位置/选中）与交互标志驱动，而非 props。
  // 追踪真正的响应式来源，定位“拖拽时哪些边在重算路径”。
  // Build the watched map only when perf is on — this runs per-edge and we don't
  // want any allocation in the disabled (production) path.
  useWhyRender(
    'AnimatedEdge',
    perfEnabled()
      ? { sourceSlice, targetSlice, selected, isInteracting, data, style }
      : EDGE_PERF_EMPTY,
  );

  if (!sourceSlice || !targetSlice) {
    return null;
  }

  const { sx, sy, tx, ty, sourcePos, targetPos } = getEdgeParams(
    getEdgeCoords(sourceSlice),
    getEdgeCoords(targetSlice),
  );
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX: sx,
    sourceY: sy,
    sourcePosition: sourcePos,
    targetX: tx,
    targetY: ty,
    targetPosition: targetPos,
  });

  // Drag-fast path: emit just the bezier + interaction overlay; skip every
  // business-related computation (dashed/disallowed label, selected menu,
  // locked badges). Cuts per-frame cost dramatically on canvases with many
  // edges adjacent to a dragged node. (model capability memo above is cheap
  // and was hoisted above this branch to satisfy rules-of-hooks.)
  if (isInteracting) {
    return (
      <>
        <BaseEdge
          id={id}
          path={edgePath}
          markerEnd={markerEnd}
          style={{
            stroke: selected ? 'var(--primary)' : 'rgba(156, 163, 175, 0.75)',
            strokeWidth: selected ? 3 : 2.5,
            ...style,
          }}
        />
        <path
          d={edgePath}
          fill="none"
          stroke="transparent"
          strokeWidth={24}
          strokeLinecap="round"
          className="react-flow__edge-interaction"
          style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
        />
      </>
    );
  }

  const sourceType = sourceSlice.type;
  const targetType = targetSlice.type;
  const frameRole = (data as any)?.frameRole as 'start' | 'end' | 'reference' | undefined;

  const isImageNode = sourceType === 'image';
  const isImageUploadNode = sourceType === 'upload' && (
    sourceSlice.fileType === 'image' ||
    (sourceSlice.contentType && String(sourceSlice.contentType).startsWith('image/'))
  );
  const imageToVideoEdge = (isImageNode || isImageUploadNode) && targetType === 'video';

  const disallowLabels = imageToVideoEdge && textOnlyModel;
  const targetFrameMode = targetSlice.frameMode;

  const isTargetLocked = Boolean(targetSlice.locked);
  const isSourceLocked = Boolean(sourceSlice.locked);

  const nodeSelected = Boolean(sourceSlice.selected || targetSlice.selected);
  const isActive = nodeSelected && (!imageToVideoEdge || !disallowLabels);
  const isHighlighted = Boolean(selected) || isActive;
  const isDashed = imageToVideoEdge && !disallowLabels && !frameRole && !targetFrameMode;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke: isHighlighted ? 'var(--primary)' : 'rgba(156, 163, 175, 0.75)',
          strokeWidth: selected ? 3 : 2.5,
          strokeDasharray: isDashed ? '6 6' : undefined,
          ...style,
        }}
      />
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={24}
        strokeLinecap="round"
        className="react-flow__edge-interaction"
        style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
      />
      {disallowLabels ? (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'none',
            }}
          >
            <div className="w-4 h-4 flex items-center justify-center rounded-full bg-background/90 border border-destructive/60 text-destructive text-[10px] leading-none shadow">
              ×
            </div>
          </div>
        </EdgeLabelRenderer>
      ) : null}
      {selected ? (
        <SelectedEdgeMenu
          edgeId={id}
          source={source}
          target={target}
          labelX={labelX}
          labelY={labelY}
          isSourceLocked={isSourceLocked}
          isTargetLocked={isTargetLocked}
        />
      ) : null}
    </>
  );
}, areAnimatedEdgePropsEqual);
AnimatedEdge.displayName = 'AnimatedEdge';
